import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { classifyPhoto, type GalleryImage } from './_galleryShared';

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Neighborhood CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Rotating image cell
// ============================================================================

const ZIGZAG_SLOT_ORDER = [0, 2, 1, 3] as const;
const SLOT_COUNT = 4;
const STEP_MS = 2200;

function photoSrc(photo: GalleryImage): string {
  return photo.image ?? photo.original_url ?? '';
}

/** Shortest distance between two indices on a circular gallery. */
function circularDistance(a: number, b: number, count: number): number {
  if (count <= 0) return 0;
  const diff = Math.abs(a - b);
  return Math.min(diff, count - diff);
}

/** Pick four starting indices as far apart as the gallery size allows. */
function computeSpreadIndices(count: number): [number, number, number, number] {
  if (count <= 0) return [0, 0, 0, 0];
  if (count === 1) return [0, 0, 0, 0];
  if (count === 2) return [0, 1, 0, 1];
  if (count === 3) return [0, 1, 2, 0];

  return Array.from({ length: SLOT_COUNT }, (_, slot) =>
    Math.floor((slot * count) / SLOT_COUNT) % count,
  ) as [number, number, number, number];
}

/**
 * Advance one slot to the next index that maximizes separation from the other
 * three cells — avoids duplicates when possible and skips in gallery-sized
 * jumps instead of stepping +1.
 */
function pickNextIndex(current: number, otherIndices: number[], count: number): number {
  if (count <= 1) return 0;

  const occupied = new Set(otherIndices);
  const jump = Math.max(1, Math.round(count / SLOT_COUNT));

  const candidates: number[] = [];
  for (let step = 1; step <= count; step++) {
    const viaJump = (current + step * jump) % count;
    if (!candidates.includes(viaJump)) candidates.push(viaJump);
  }
  for (let step = 1; step <= count; step++) {
    const sequential = (current + step) % count;
    if (!candidates.includes(sequential)) candidates.push(sequential);
  }

  let best = candidates[0] ?? (current + 1) % count;
  let bestScore = -Infinity;

  for (const candidate of candidates) {
    const isDuplicate = occupied.has(candidate);
    const minDist =
      otherIndices.length === 0
        ? count
        : Math.min(...otherIndices.map((other) => circularDistance(candidate, other, count)));

    const score = (isDuplicate ? -10_000 : 0) + minDist * 100 + (candidate === current ? -1 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return best;
}

interface GridImageCellProps {
  photos: GalleryImage[];
  currentIndex: number;
  className?: string;
}

function GridImageCell({ photos, currentIndex, className }: GridImageCellProps) {
  const count = photos.length;
  const safeCurrent = count > 0 ? currentIndex % count : 0;

  return (
    <div className={`neighborhood-grid-cell ${className ?? ''}`.trim()}>
      {count === 0 ? (
        <div className="absolute inset-0 bg-white" aria-hidden="true" />
      ) : (
        photos.map((photo, i) => {
          const src = photoSrc(photo);
          if (!src) return null;
          const alt = photo.alt_text || photo.title || 'Community photo';
          return (
            <VibesitesImage
              key={`${src}-${i}`}
              url={src}
              alt={alt}
              sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 50vw"
              className={`neighborhood-grid-cell__img${i === safeCurrent ? ' neighborhood-grid-cell__img--active' : ''}`}
              priority={i === safeCurrent}
            />
          );
        })
      )}
    </div>
  );
}

function useZigzagRotation(count: number, paused: boolean) {
  const [cellIndices, setCellIndices] = useState<[number, number, number, number]>(() =>
    computeSpreadIndices(0),
  );
  const stepRef = useRef(0);

  useEffect(() => {
    setCellIndices(computeSpreadIndices(count));
    stepRef.current = 0;
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;

    const id = setInterval(() => {
      const slot = ZIGZAG_SLOT_ORDER[stepRef.current % ZIGZAG_SLOT_ORDER.length];
      stepRef.current += 1;
      setCellIndices((prev) => {
        const others = [0, 1, 2, 3]
          .filter((i) => i !== slot)
          .map((i) => prev[i]);
        const next = [...prev] as [number, number, number, number];
        next[slot] = pickNextIndex(prev[slot], others, count);
        return next;
      });
    }, STEP_MS);

    return () => clearInterval(id);
  }, [count, paused]);

  return cellIndices;
}

// ============================================================================
// Component
// ============================================================================

export function NeighborhoodSection() {
  const photos = useCollectionBinding<GalleryImage>('neighborhoodsection.photos', {
    collection: 'photo_gallery',
  });

  const communityPhotos = useMemo(() => {
    const list = photos && photos.length > 0 ? photos : [];
    return list.filter((photo) => {
      if (classifyPhoto(photo) !== 'community') return false;
      return Boolean(photoSrc(photo));
    });
  }, [photos]);

  const cta = useSlotGroup('neighborhoodsection.cta_link', ctaLinkDef);
  const [paused, setPaused] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const gridPaused = paused || reduceMotion;
  const cellIndices = useZigzagRotation(communityPhotos.length, gridPaused);

  useRevealStyles();
  useCtaSheenStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full overflow-hidden bg-secondary py-10 md:py-12 lg:py-14"
    >
      <div className="mx-auto flex flex-col items-start gap-8 px-4 sm:px-6 lg:grid lg:max-w-none lg:grid-cols-2 lg:items-start lg:gap-6 lg:px-0 xl:gap-10">
        {/* Left: copy on secondary surface — bleeds to viewport left, ends at center on lg+ */}
        <div className="neighborhood-copy relative z-10 -mx-4 flex w-[calc(100%+2rem)] flex-col px-8 py-8 sm:-mx-6 sm:w-[calc(100%+3rem)] sm:px-10 sm:py-10 md:px-12 md:py-12 lg:mx-0 lg:w-full lg:px-12 lg:pr-14 xl:px-16 xl:pr-20">
          <RichTextContent
              path="neighborhoodsection.eyebrow"
              label="Eyebrow"
              defaultValue="<p>Ideally Located</p>"
              as="p"
              className="neighborhood-eyebrow font-body mb-4 text-xs uppercase tracking-[0.25em] sm:text-sm"
            />

            <RichTextContent
              path="neighborhoodsection.heading"
              label="Heading"
              defaultValue='<p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>'
              as="h2"
              className="neighborhood-heading font-heading mb-6 text-3xl font-bold uppercase leading-[1.1] sm:text-4xl md:text-[2.75rem]"
            />

            <div className="mb-8 h-1 w-16 rounded-full bg-primary" aria-hidden="true" />

            <RichTextContent
              path="neighborhoodsection.body"
              label="Body Text"
              defaultValue="<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>"
              as="div"
              className="neighborhood-body font-body mb-10 max-w-xl text-base leading-relaxed [&_a]:underline"
            />

            <EditableSlotGroup
              groupPath="neighborhoodsection.cta_link"
              definition={ctaLinkDef}
              className="inline-block"
            >
              <a
                href={cta.url || '/neighborhood'}
                target={cta._target || '_self'}
                className="neighborhood-cta vantage-cta-sheen inline-flex items-center justify-center px-8 py-3.5 font-heading text-sm uppercase tracking-wider"
              >
                <span className="relative z-10">
                  <RichTextContent
                    path="neighborhoodsection.cta_label"
                    label="CTA Label"
                    defaultValue="<p>Explore the Neighborhood</p>"
                    as="span"
                  />
                </span>
              </a>
          </EditableSlotGroup>
        </div>

        {/* Right: 2×2 auto-rotating community photo grid */}
        <div
          className="neighborhood-photo-grid relative z-0 grid w-full max-w-xl grid-cols-2 gap-2.5 sm:max-w-none sm:gap-3 lg:max-w-none lg:pl-4 lg:pr-8 xl:pr-12"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <GridImageCell photos={communityPhotos} currentIndex={cellIndices[0]} />
          <GridImageCell photos={communityPhotos} currentIndex={cellIndices[1]} />
          <GridImageCell photos={communityPhotos} currentIndex={cellIndices[2]} />
          <GridImageCell photos={communityPhotos} currentIndex={cellIndices[3]} />
        </div>
      </div>

      <style>{`
        /* Copy sits on bg-secondary — use text-on-secondary so eyebrow/
           heading/body (and field tokens) stay readable on the brand color. */
        .neighborhood-copy {
          --neighborhood-copy-ink: var(--vibe-color-text-on-secondary);
        }

        .neighborhood-eyebrow {
          color: var(--neighborhood-copy-ink) !important;
        }

        .neighborhood-heading {
          color: var(--neighborhood-copy-ink) !important;
        }

        .neighborhood-body,
        .neighborhood-body [data-vibesite-richtext],
        .neighborhood-body [data-vibesite-richtext] p,
        .neighborhood-body [data-vibesite-richtext] span,
        .neighborhood-body [data-type="field-token"] {
          color: var(--neighborhood-copy-ink) !important;
        }

        .neighborhood-body a,
        .neighborhood-body a * {
          color: #fff !important;
        }

        .neighborhood-eyebrow [data-vibesite-richtext],
        .neighborhood-eyebrow [data-vibesite-richtext] p,
        .neighborhood-eyebrow [data-vibesite-richtext] span,
        .neighborhood-eyebrow [data-type="field-token"],
        .neighborhood-eyebrow strong,
        .neighborhood-eyebrow em,
        .neighborhood-heading [data-vibesite-richtext],
        .neighborhood-heading [data-vibesite-richtext] p,
        .neighborhood-heading [data-vibesite-richtext] span,
        .neighborhood-heading [data-type="field-token"],
        .neighborhood-heading strong,
        .neighborhood-heading em {
          color: var(--neighborhood-copy-ink) !important;
        }

        .neighborhood-cta [data-vibesite-richtext],
        .neighborhood-cta [data-vibesite-richtext] p,
        .neighborhood-cta [data-vibesite-richtext] span {
          margin: 0;
          font: inherit;
          color: inherit;
          text-transform: inherit;
          letter-spacing: inherit;
        }

        /* Primary CTA so the button contrasts against the secondary section. */
        .neighborhood-cta.vantage-cta-sheen {
          --vantage-cta-sheen-accent: var(--vibe-color-primary);
          --vantage-cta-sheen-on-accent: var(--vibe-color-text-on-primary);
          --_cta-sheen-accent: var(--vibe-color-primary);
          --_cta-sheen-on-accent: var(--vibe-color-text-on-primary);
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }

        .neighborhood-grid-cell {
          position: relative;
          aspect-ratio: 1 / 1;
          width: 100%;
          overflow: hidden;
          background: #fff;
        }
        .neighborhood-grid-cell__img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          opacity: 0;
          transition: opacity 0.7s ease;
        }
        .neighborhood-grid-cell__img--active {
          opacity: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .neighborhood-grid-cell__img {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodSection',
  displayName: 'Neighborhood Section',
  tokens: {
    'neighborhoodsection.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Ideally Located</p>',
    },
    'neighborhoodsection.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'neighborhoodsection.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>',
    },
    'neighborhoodsection.cta_label': {
      label: 'CTA Label',
      type: 'richtext',
      defaultValue: '<p>Explore the Neighborhood</p>',
    },
  },
  slotGroups: {
    'neighborhoodsection.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'neighborhoodsection.photos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      description:
        'Community gallery photos that auto-rotate in the 2×2 grid. Each cell starts on a different photo and cycles through the set.',
    },
  },
};

(NeighborhoodSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodSection;
